import { getGuideSurfaceSection, type GuideSurfaceSection } from '../features/guides';

export type AssistantGuideStep = {
  step: number;
  title: string;
  description: string;
  actions: string[];
  callScript?: string | null;
  tip?: string | null;
};

export type AssistantGuideRecord = {
  id: string;
  kind: 'playbook' | 'content-card';
  section: string;
  title: string;
  summary: string;
  body?: string | null;
  detail?: string | null;
  steps?: AssistantGuideStep[];
};

export type AssistantProfileContext = {
  firstName?: string | null;
  partnerName?: string | null;
  postcode?: string | null;
  children?: { name: string }[];
};

export type AssistantGuideMatch = {
  id: string;
  kind: 'playbook' | 'content-card';
  section: GuideSurfaceSection;
  title: string;
};

export type AssistantDraft = {
  title: string;
  body: string;
  filename: string;
};

export type AssistantResponse = {
  headline: string;
  summary: string;
  nextSteps: string[];
  relevantGuides: AssistantGuideMatch[];
  draft: AssistantDraft | null;
};

type DraftScenario = {
  key: string;
  title: string;
  keywords: string[];
  summary: string;
  nextSteps: string[];
  buildDraft: (context: AssistantProfileContext) => string;
};

const DRAFT_INTENT_KEYWORDS = [
  'draft',
  'letter',
  'email',
  'write',
  'complain',
  'appeal',
  'request',
  'respond',
];

const DRAFT_SCENARIOS: DraftScenario[] = [
  {
    key: 'benefits-appeal',
    title: 'Benefits decision appeal draft',
    keywords: ['benefit', 'benefits', 'dwp', 'decision', 'appeal', 'mandatory reconsideration', 'pip', 'universal credit'],
    summary: 'Start with a short written challenge that says which decision you are disputing, why it is wrong, and what evidence will follow.',
    nextSteps: [
      'Name the decision and the date you were told about it.',
      'List the main reason you think it is wrong in plain English.',
      'Say what evidence you are sending now and what will follow.',
    ],
    buildDraft: (context) => `To whom it may concern,

I am asking for a mandatory reconsideration of the decision dated [decision date] about my benefit claim.

I believe the decision is wrong because:
- [main reason one]
- [main reason two]
- [main reason three]

Please review the decision again. I am sending the supporting information I already have and I will send any further evidence as soon as possible.

Please confirm that this request has been logged and let me know if you need anything else from me.

Thanks,
${closingName(context)}`,
  },
  {
    key: 'landlord-repair',
    title: 'Landlord repair complaint draft',
    keywords: ['landlord', 'repair', 'boiler', 'heating', 'damp', 'mould', 'mold', 'broken boiler', 'tenancy'],
    summary: 'Keep the message factual: say what is broken, how long it has been unresolved, and the impact on the household.',
    nextSteps: [
      'Describe the repair problem and the first date you reported it.',
      'Say what is still unresolved right now.',
      'Ask for a response date and the next practical step in writing.',
    ],
    buildDraft: (context) => `Hi,

I am writing to complain about the unresolved repair issue at my home: [describe the problem clearly].

I first reported this on [date] and it is still unresolved. The impact right now is:
- [impact on heating / hot water / safety / damp / mould / access]
- [impact on children or household]
- [any temporary costs or practical disruption]

Please confirm in writing:
- when the repair will be inspected or completed
- what temporary action will be taken in the meantime
- who I should contact if this is not resolved as promised

Please treat this as a formal written complaint and respond as soon as possible.

Thanks,
${closingName(context)}`,
  },
  {
    key: 'payment-plan',
    title: 'Payment plan request draft',
    keywords: ['payment plan', 'arrears', 'debt', 'creditor', 'collector', 'collections', 'repayment'],
    summary: 'Lead with what you can realistically afford, not what you wish were possible. Keep the payment request short and concrete.',
    nextSteps: [
      'State the account or reference number.',
      'Offer the payment you can actually sustain each month.',
      'Ask for interest, fees, or collections activity to pause while the plan is reviewed.',
    ],
    buildDraft: (context) => `Hi,

I am writing about account/reference [reference number] to ask for a payment plan.

My circumstances mean I cannot pay the balance in the current way. I can afford £[amount] per [week/month] at the moment and I am asking you to accept that arrangement while I stabilise things.

Please confirm:
- whether you will accept this payment plan
- whether interest, fees, or collections activity can be paused while it is in place
- how I should make the payments

Please respond in writing so I have the arrangement clearly recorded.

Thanks,
${closingName(context)}`,
  },
  {
    key: 'flexible-working',
    title: 'Flexible working request draft',
    keywords: ['flexible working', 'flexibility', 'adjustments', 'caring', 'return to work', 'phased return'],
    summary: 'Ask for the specific work pattern or adjustment that would help and keep the next step visible in writing.',
    nextSteps: [
      'Name the specific change that would help most.',
      'Explain the practical reason briefly without over-explaining.',
      'Ask for a conversation date and written confirmation of next steps.',
    ],
    buildDraft: (context) => `Hi,

I am writing to ask for a conversation about a flexible working arrangement / adjustment that would help me manage work sustainably.

The change I would like to discuss is:
- [hours / start and finish times / pattern / phased return / specific adjustment]

This would help because:
- [brief practical reason]

Please let me know a suitable time to discuss this and confirm the next step in writing afterwards.

Thanks,
${closingName(context)}`,
  },
  {
    key: 'work-grievance',
    title: 'Work grievance draft',
    keywords: ['grievance', 'disciplinary', 'appeal work', 'redundancy', 'work issue', 'formal complaint', 'employment'],
    summary: 'Keep the draft calm and chronological. State what happened, the impact, and what outcome or process you are asking for.',
    nextSteps: [
      'List the key facts and dates in order.',
      'Describe the impact without adding unnecessary side detail.',
      'Ask for the formal process, meeting, or appeal route in writing.',
    ],
    buildDraft: (context) => `Hi,

I am writing to raise a formal concern about [issue].

The key facts are:
- [what happened]
- [dates / meetings / messages]
- [impact on you]

Please treat this under the appropriate formal process and confirm:
- the next steps
- who will handle it
- any meeting or deadline I need to know about

If you need any further information from me, let me know.

Thanks,
${closingName(context)}`,
  },
];

function closingName(context: AssistantProfileContext) {
  return context.firstName?.trim() || 'Your name';
}

function normaliseText(value: string) {
  return value.toLowerCase().replace(/[^a-z0-9\s-]/g, ' ');
}

function tokenize(value: string) {
  return Array.from(new Set(normaliseText(value).split(/\s+/).filter((token) => token.length > 1)));
}

function includesPhrase(text: string, phrase: string) {
  const normalisedText = normaliseText(text);
  const normalisedPhrase = normaliseText(phrase);
  return normalisedText.includes(normalisedPhrase);
}

function guideSearchText(guide: AssistantGuideRecord) {
  const stepText = (guide.steps || [])
    .map((step) => [step.title, step.description, ...step.actions, step.callScript || '', step.tip || ''].join(' '))
    .join(' ');

  return [guide.title, guide.summary, guide.body || '', guide.detail || '', stepText].join(' ');
}

function rankGuide(query: string, tokens: string[], guide: AssistantGuideRecord) {
  const haystack = guideSearchText(guide);
  const normalisedHaystack = normaliseText(haystack);
  let score = 0;

  for (const token of tokens) {
    if (token.length < 3) continue;
    if (normalisedHaystack.includes(token)) {
      score += guide.title.toLowerCase().includes(token) ? 5 : 2;
    }
  }

  if (includesPhrase(query, guide.title)) {
    score += 8;
  }

  if (guide.kind === 'playbook' && score > 0) {
    score += 2;
  }

  return score;
}

function topGuideActions(guide: AssistantGuideRecord) {
  if (guide.kind === 'content-card') {
    return [guide.summary, guide.body, guide.detail].filter((value): value is string => Boolean(value)).slice(0, 2);
  }

  return (guide.steps || [])
    .flatMap((step) => [step.title, ...step.actions])
    .filter((value) => value.trim().length > 0)
    .slice(0, 3);
}

function detectDraftScenario(query: string) {
  const wantsDraft = DRAFT_INTENT_KEYWORDS.some((keyword) => includesPhrase(query, keyword));
  if (!wantsDraft) {
    return null;
  }

  return (
    DRAFT_SCENARIOS.find((scenario) => scenario.keywords.some((keyword) => includesPhrase(query, keyword)))
    || null
  );
}

function buildFallbackSummary(query: string, guideMatches: AssistantGuideRecord[]) {
  if (guideMatches.length === 0) {
    return {
      headline: 'Ask Adulting',
      summary: `Adulting could not find an exact saved guide for “${query.trim()}” yet, so start with the nearest section guide and keep the first written step short.`,
      nextSteps: [
        'Open the closest guide below and use the first concrete step, not the whole pack at once.',
        'If you need to write to someone, keep it factual and ask for the next step in writing.',
        'Add the date to Calendar if it becomes time-sensitive.',
      ],
    };
  }

  const topGuide = guideMatches[0];
  return {
    headline: topGuide.title,
    summary: topGuide.summary || topGuide.body || 'Adulting found the closest guide and pulled the first practical next steps forward.',
    nextSteps: topGuideActions(topGuide),
  };
}

export function buildAdultingAssistantResponse({
  query,
  guides,
  context,
}: {
  query: string;
  guides: AssistantGuideRecord[];
  context?: AssistantProfileContext;
}): AssistantResponse | null {
  const trimmedQuery = query.trim();
  if (!trimmedQuery) {
    return null;
  }

  const tokens = tokenize(trimmedQuery);
  const scoredGuides = guides
    .map((guide) => ({ guide, score: rankGuide(trimmedQuery, tokens, guide) }))
    .filter((entry) => entry.score > 0)
    .sort((left, right) => right.score - left.score)
    .slice(0, 3);
  const matchedGuides = scoredGuides.map((entry) => entry.guide);
  const draftScenario = detectDraftScenario(trimmedQuery);
  const base = draftScenario
    ? {
        headline: draftScenario.title,
        summary: draftScenario.summary,
        nextSteps: draftScenario.nextSteps,
      }
    : buildFallbackSummary(trimmedQuery, matchedGuides);

  return {
    headline: base.headline,
    summary: base.summary,
    nextSteps: base.nextSteps.filter((value) => value.trim().length > 0).slice(0, 4),
    relevantGuides: matchedGuides
      .map((guide) => {
        const section = getGuideSurfaceSection(guide.section);
        if (!section) return null;
        return {
          id: guide.id,
          kind: guide.kind,
          section,
          title: guide.title,
        } satisfies AssistantGuideMatch;
      })
      .filter((value): value is AssistantGuideMatch => Boolean(value)),
    draft: draftScenario
      ? {
          title: draftScenario.title,
          body: draftScenario.buildDraft(context || {}),
          filename: `${draftScenario.key}.txt`,
        }
      : null,
  };
}
